import React, { memo } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import _isEqual from 'lodash/isEqual';

import StatusBadge from 'components/StatusBadge';
import FraudScore from 'components/FraudScore';
import visa_logo from 'assets/icons/visa_blue.svg';

import { SendReceipt, RefundPayment, CapturePayment } from './buttons';
import { transactionSummary as TEXT } from 'locales/en/transaction';
import {
  STATUS_SETTLED,
  STATUS_PARTIALLY_REFUNDED,
  STATUS_REFUNDED,
  STATUS_AUTHORIZED,
} from '../helpers';

const TITLES = [
  TEXT.date,
  TEXT.customer_name,
  TEXT.paymentMethod,
  TEXT.fraudScore,
];

const Summary = ({ transaction }) => {
  const {
    created_at,
    shopper_name,
    risk_score,
    amountToDisplay,
    last4,
    status,
  } = transaction;

  const renderButton = () => {
    const isRefundable = [STATUS_SETTLED, STATUS_PARTIALLY_REFUNDED].some(
      (item) => item === transaction.status
    );
    if (transaction.is_being_adjusted) {
      return (
        <button className="btn btn-secondary w-40 h-11 px-0" disabled>
          {TEXT.adjusting}
        </button>
      );
    } else if (transaction.is_being_captured) {
      return (
        <button className="btn btn-secondary w-40 h-11 px-0" disabled>
          {TEXT.capturing}
        </button>
      );
    } else if (transaction.status === STATUS_AUTHORIZED) {
      return <CapturePayment transaction={transaction} />;
    } else if (isRefundable) {
      return <RefundPayment transaction={transaction} />;
    } else if (transaction.status === STATUS_REFUNDED) {
      return <SendReceipt transaction={transaction} />;
    }
  };

  return (
    <div className="card mb-8 p-0">
      <div className="p-6 lg:p-12 border-b border-javelin-gray-border">
        <p className="text-javelin-gray-light text-xs mb-3">{TEXT.payment}</p>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <span className="font-bold text-3xl">{amountToDisplay}</span>
            <StatusBadge status={status} />
          </div>
          {renderButton()}
        </div>
      </div>
      <div className="p-6 lg:p-12 grid grid-cols-4 lg:w-10/12 gap-2">
        {TITLES.map((title) => (
          <div key={title} className="flex" data-testid={title}>
            <span className="text-xs text-javelin-gray-light">{title}</span>
          </div>
        ))}
        <span className="text-sm text-javelin-gray-dark">
          {format(new Date(created_at), 'dd MMM, H:m')}
        </span>
        <span className="text-sm text-javelin-gray-dark">{shopper_name}</span>
        <span>
          <div className="flex items-center text-sm text-javelin-gray-dark">
            <img
              className="w-6 ml-0 mr-1 overflow-visible inline rounded-sm"
              src={visa_logo}
              alt={`visa ending in ${last4}`}
            />
            <span className="h-full">ending in {last4}</span>
          </div>
        </span>
        <FraudScore value={risk_score} />
      </div>
    </div>
  );
};

Summary.propTypes = {
  transaction: PropTypes.shape({}),
};
Summary.defaultProps = {
  transaction: {},
};

const areEqual = (prevProps, nextProps) => {
  return _isEqual(prevProps.transaction, nextProps.transaction);
};

export default memo(Summary, areEqual);
